<script setup>
import { computed, ref, watch } from 'vue';
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css'
import './vueDatePickerTheme.css'

const emit = defineEmits(['update:time', 'timeSelected']);

const props = defineProps({
    // Wall-clock time as 'HH:mm'
    time: {
        type: String,
        required: false,
        default: null
    },
    showInline: {
        type: Boolean,
        required: false,
        default: false
    },
    minutesIncrement: {
        type: Number,
        required: false,
        default: 1
    },
    required: {
        type: Boolean,
        required: false,
        default: false
    },
    // Lets the user type the time instead of only picking it; has no effect inline, where there is no input
    textInput: {
        type: Boolean,
        required: false,
        default: false
    }
})

// Each pattern is tried in turn. The picker trims a pattern to the typed length, so '9', '9:30' and '0930' all work.
const textInputOptions = computed(() => props.textInput ? { format: ['HH:mm', 'HHmm'] } : false)

const toPickerValue = (time) => {
    if (!time) {
        return null;
    }

    const [hours, minutes] = time.split(':').map(Number);

    return { hours, minutes, seconds: 0 };
}

const toTimeString = (value) => {
    if (!value) {
        return null;
    }

    const hours = String(value.hours).padStart(2, '0')
    const minutes = String(value.minutes).padStart(2, '0')
    return `${hours}:${minutes}`
}

const pickerValue = ref(toPickerValue(props.time));

watch(() => props.time, (newValue) => {
    if (newValue !== toTimeString(pickerValue.value)) {
        pickerValue.value = toPickerValue(newValue);
    }
})

const handleTime = (newValue) => {
    emit('update:time', toTimeString(newValue));
    emit('timeSelected');
}
</script>
<template>
    <VueDatePicker
        v-model="pickerValue"
        :time-picker="true"
        :inline="props.showInline"
        :auto-apply="true"
        :is-24="true"
        :minutes-increment="minutesIncrement"
        :required="props.required"
        :text-input="textInputOptions"
        format="HH:mm"
        locale="en-US"
        :select-text="_mft('shared:select')"
        @update:model-value="handleTime"
        class="font-body"
    >
        <template #input-icon>
            <!-- VueDatePicker's own clock icon, so it matches the calendar icon in input-datepicker -->
            <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 32 32"
                fill="currentColor"
                aria-hidden="true"
                class="dp__icon dp__input_icons"
            >
                <path d="M16 1.333c-8.095 0-14.667 6.572-14.667 14.667s6.572 14.667 14.667 14.667c8.095 0 14.667-6.572 14.667-14.667s-6.572-14.667-14.667-14.667zM16 4c6.623 0 12 5.377 12 12s-5.377 12-12 12c-6.623 0-12-5.377-12-12s5.377-12 12-12z" />
                <path d="M14.667 8v8c0 0.505 0.285 0.967 0.737 1.193l5.333 2.667c0.658 0.329 1.46 0.062 1.789-0.596s0.062-1.46-0.596-1.789l-4.596-2.298c0 0 0-7.176 0-7.176 0-0.736-0.597-1.333-1.333-1.333s-1.333 0.597-1.333 1.333z" />
            </svg>
        </template>
    </VueDatePicker>
</template>
